import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import TeacherClasses from "./pages/teacher/TeacherClasses";
import TeacherClassDetail from "./pages/teacher/TeacherClassDetail";
import TeacherCourses from "./pages/teacher/TeacherCourses";
import TeacherCourseDetail from "./pages/teacher/TeacherCourseDetail";
import CourseMaterialManagement from "./pages/teacher/CourseMaterialManagement";
import CourseContentManagement from "./pages/elearning/CourseContentManagement";
import TeacherStudents from "./pages/teacher/TeacherStudents";
import TeacherAttendance from "./pages/teacher/TeacherAttendance";
import TeacherAssessments from "./pages/teacher/TeacherAssessments";
import TeacherProfile from "./pages/teacher/TeacherProfile";
import TeacherSchedule from "./pages/teacher/TeacherSchedule";
import TeacherAnnouncements from "./pages/teacher/TeacherAnnouncements";
import TeacherAnalytics from "./pages/teacher/TeacherAnalytics";

// Exams & Grades
import TeacherExamsGrades from "./pages/teacher/TeacherExamsGrades";

// E-Learning components
import ELearningRouter from "./components/elearning/ELearningRouter";
import TeacherCourseEdit from "./pages/elearning/TeacherCourseEdit";

// Student components
import StudentDashboard from "./pages/student/StudentDashboard";
import ECourseDashboard from "./pages/student/ECourseDashboard";
import StudentProfile from "./pages/student/StudentProfile";
import StudentGrades from "./pages/student/StudentGrades";
import StudentAttendance from "./pages/student/StudentAttendance";
import StudentFees from "./pages/student/StudentFees";
import { StudentCourses } from "./pages/student/StudentCourses";
import StudentSchedule from "./pages/student/StudentSchedule";
import StudentNotices from "./pages/student/StudentNotices";
import LiveSessions from "./pages/student/LiveSessions";

// Student E-Learning components
import StudentElearningDashboard from "./pages/student/StudentElearningDashboard";
import StudentEcourseCatalog from "./pages/student/StudentEcourseCatalog";
import StudentCourseViewer from "./pages/student/StudentCourseViewer";
import StudentQuizzes from "./pages/student/StudentQuizzes";
import MyEcourses from "./pages/student/MyEcourses";
import CourseDetail from "./pages/student/CourseDetail";
import ModuleViewer from "./pages/elearning/ModuleViewer";

// Enhanced Quiz Components
import { QuizTaking, QuizResults } from "./components/elearning/quiz";

import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import NotificationManager from "./components/NotificationManager";

import Layout from "./components/common/Layout";
import LandingPage from "./pages/LandingPage";
import WelcomePage from "./pages/WelcomePage";
import LoginPage from "./pages/LoginPage";
import ECourseRegistrationPage from "./pages/ECourseRegistrationPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import Teachers from "./pages/Teachers";
import AdminFees from "./pages/admin/AdminFees";
import Expenses from "./pages/Expenses";
import Attendance from "./pages/Attendance";
import Classes from "./pages/Classes";
import Courses from "./pages/Courses";
import AdminPanel from "./pages/AdminPanel";
import SuperAdminPanel from "./pages/SuperAdminPanel";
import TermManagement from "./pages/superadmin/TermManagement";
import LandingPageManagement from "./pages/LandingPageManagement";
import BranchAdminDashboard from "./pages/BranchAdminDashboard";
import BranchSettings from "./pages/BranchSettings";
import AdminStudents from "./pages/admin/AdminStudents";
import AdminTeachers from "./pages/admin/AdminTeachers";
import AdminClasses from "./pages/admin/AdminClasses";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminDepartments from "./pages/admin/AdminDepartments";
import AdminExpenses from "./pages/admin/AdminExpenses";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import BranchReports from "./pages/admin/BranchReports";
import { AdminECourseApprovals } from "./pages/admin/AdminECourseApprovals";
import AdminECourseAnalytics from "./pages/admin/AdminECourseAnalytics";
import AdminECourseStudents from "./pages/admin/AdminECourseStudents";
import ManualEnrollment from "./pages/admin/ManualEnrollment";
import BranchManagement from "./pages/BranchManagement";
import UserManagement from "./pages/UserManagement";
import ExpenseManagement from "./pages/ExpenseManagement";
import FinancialReports from "./pages/FinancialReports";
import SystemAnalytics from "./pages/SystemAnalytics";
import GlobalSettings from "./pages/GlobalSettings";
import SecurityRoles from "./pages/SecurityRoles";
import AuditLogs from "./pages/AuditLogs";
import DebugAuth from "./pages/DebugAuth";
import SecretaryDashboard from "./pages/secretary/SecretaryDashboard";
import SecretaryStudents from "./pages/secretary/SecretaryStudents";
import SecretaryFees from "./pages/secretary/SecretaryFees";
import SecretarySchedule from "./pages/secretary/SecretarySchedule";
import SecretaryAnnouncements from "./pages/secretary/SecretaryAnnouncements";
import SecretaryAttendance from "./pages/secretary/SecretaryAttendance";
import SecretaryReports from "./pages/secretary/SecretaryReports";
import SecretaryExpenses from "./pages/secretary/SecretaryExpenses";
import ProtectedRoute from "./components/common/ProtectedRoute";
import RoleGuard from "./components/common/RoleGuard";
import { AuthProvider } from "./contexts/AuthContext";
import { BranchProvider } from "./contexts/BranchContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import OfflineIndicator from "./components/OfflineIndicator";
import IOSInstallPrompt from "./components/common/IOSInstallPrompt";
import OneSignal from "react-onesignal";

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log("beforeinstallprompt event fired");
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);

      // Check if SW is registered
      navigator.serviceWorker.getRegistration().then((registration) => {
        console.log("SW registration:", registration);
        if (registration) {
          console.log("SW state:", registration.active?.state);
        }
      });
    };

    const handleAppInstalled = () => {
      console.log("App was installed");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // OneSignal initialization is now handled in AuthContext after login
    // to prevent subscription prompts before user authentication

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response: ${outcome}`);

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <OfflineProvider>
          <AuthProvider>
            <BranchProvider>
              <OfflineIndicator />
              <IOSInstallPrompt />
              <NotificationManager />
              <div data-theme="atiam" className="max-h-screen overflow-clip">
                {showInstallButton && (
                  <div className="fixed bottom-4 right-4 z-50">
                    <button
                      onClick={handleInstallClick}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Install App
                    </button>
                  </div>
                )}
                <Routes>
                  <Route path="/" element={<WelcomePage />} />
                  <Route path="/landing" element={<LandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/register/ecourse"
                    element={<ECourseRegistrationPage />}
                  />
                  <Route
                    path="/reset-password/:token"
                    element={<PasswordResetPage />}
                  />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Student routes - prioritized for proper role-based routing */}
                    <Route
                      path="student/*"
                      element={
                        <RoleGuard
                          allowedRoles={["student", "admin", "superadmin"]}
                        >
                          <Routes>
                            <Route index element={<StudentDashboard />} />
                            <Route
                              path="dashboard"
                              element={<StudentDashboard />}
                            />
                            <Route
                              path="ecourse-dashboard"
                              element={<ECourseDashboard />}
                            />
                            <Route
                              path="profile"
                              element={<StudentProfile />}
                            />
                            <Route path="grades" element={<StudentGrades />} />
                            <Route
                              path="attendance"
                              element={<StudentAttendance />}
                            />
                            <Route path="fees" element={<StudentFees />} />
                            <Route
                              path="courses"
                              element={<StudentCourses />}
                            />
                            <Route
                              path="schedule"
                              element={<StudentSchedule />}
                            />
                            <Route
                              path="notices"
                              element={<StudentNotices />}
                            />

                            {/* E-Learning Routes */}
                            <Route
                              path="elearning/*"
                              element={
                                <Routes>
                                  <Route
                                    index
                                    element={<StudentElearningDashboard />}
                                  />
                                  <Route
                                    path="dashboard"
                                    element={<StudentElearningDashboard />}
                                  />
                                  <Route
                                    path="my-courses"
                                    element={<MyEcourses />}
                                  />
                                  <Route
                                    path="catalog"
                                    element={<StudentEcourseCatalog />}
                                  />
                                  <Route
                                    path="courses"
                                    element={<StudentEcourseCatalog />}
                                  />
                                  <Route
                                    path="course/:courseId"
                                    element={<CourseDetail />}
                                  />
                                  <Route
                                    path="course/:courseId/start"
                                    element={<StudentCourseViewer />}
                                  />
                                  <Route
                                    path="course/:courseId/continue"
                                    element={<StudentCourseViewer />}
                                  />
                                  <Route
                                    path="courses/:courseId"
                                    element={<StudentCourseViewer />}
                                  />
                                  <Route
                                    path="course/:courseId/module/:moduleId"
                                    element={<ModuleViewer />}
                                  />
                                  <Route
                                    path="quizzes"
                                    element={<StudentQuizzes />}
                                  />
                                  <Route
                                    path="quizzes/:quizId"
                                    element={<QuizTaking />}
                                  />
                                  <Route
                                    path="quizzes/:quizId/results/:attemptId"
                                    element={<QuizResults />}
                                  />
                                  <Route
                                    path="live-sessions"
                                    element={<LiveSessions />}
                                  />
                                </Routes>
                              }
                            />
                          </Routes>
                        </RoleGuard>
                      }
                    />

                    {/* Teacher routes - prioritized for proper role-based routing */}
                    <Route
                      path="teacher/*"
                      element={
                        <RoleGuard
                          allowedRoles={["teacher", "admin", "superadmin"]}
                        >
                          <Routes>
                            <Route index element={<TeacherDashboard />} />
                            <Route
                              path="dashboard"
                              element={<TeacherDashboard />}
                            />
                            <Route
                              path="classes"
                              element={<TeacherClasses />}
                            />
                            <Route
                              path="classes/:id"
                              element={<TeacherClassDetail />}
                            />
                            <Route
                              path="courses"
                              element={<TeacherCourses />}
                            />
                            <Route
                              path="courses/:id"
                              element={<TeacherCourseDetail />}
                            />
                            <Route
                              path="courses/:courseId/materials"
                              element={<CourseMaterialManagement />}
                            />
                            <Route
                              path="schedule"
                              element={<TeacherSchedule />}
                            />
                            <Route
                              path="students"
                              element={<TeacherStudents />}
                            />
                            <Route
                              path="attendance"
                              element={<TeacherAttendance />}
                            />
                            <Route
                              path="assessments"
                              element={<TeacherAssessments />}
                            />
                            <Route
                              path="announcements"
                              element={<TeacherAnnouncements />}
                            />
                            <Route
                              path="analytics"
                              element={<TeacherAnalytics />}
                            />
                            <Route
                              path="grades"
                              element={<TeacherExamsGrades />}
                            />
                            <Route
                              path="exams-grades"
                              element={<TeacherExamsGrades />}
                            />
                            <Route
                              path="profile"
                              element={<TeacherProfile />}
                            />
                          </Routes>
                        </RoleGuard>
                      }
                    />

                    {/* E-Learning routes - for Students, Teachers, and Branch Admins */}
                    <Route
                      path="elearning/*"
                      element={
                        <RoleGuard
                          allowedRoles={[
                            "student",
                            "teacher",
                            "branchadmin",
                            "admin",
                            "superadmin",
                          ]}
                        >
                          <ELearningRouter />
                        </RoleGuard>
                      }
                    />

                    {/* General routes - protected by role guards */}
                    <Route
                      path="dashboard"
                      element={
                        <RoleGuard
                          allowedRoles={[
                            "admin",
                            "branchadmin",
                            "secretary",
                            "superadmin",
                          ]}
                        >
                          <Dashboard />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="students"
                      element={
                        <RoleGuard
                          allowedRoles={[
                            "admin",
                            "branchadmin",
                            "secretary",
                            "teacher",
                            "superadmin",
                          ]}
                        >
                          <Students />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="teachers"
                      element={
                        <RoleGuard
                          allowedRoles={["admin", "branchadmin", "superadmin"]}
                        >
                          <Teachers />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="fees"
                      element={
                        <RoleGuard
                          allowedRoles={[
                            "admin",
                            "branchadmin",
                            "secretary",
                            "superadmin",
                          ]}
                        >
                          <AdminFees />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="expenses"
                      element={
                        <RoleGuard
                          allowedRoles={["admin", "branchadmin", "superadmin"]}
                        >
                          <Expenses />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="classes"
                      element={
                        <RoleGuard
                          allowedRoles={[
                            "admin",
                            "branchadmin",
                            "teacher",
                            "superadmin",
                          ]}
                        >
                          <Classes />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="courses"
                      element={
                        <RoleGuard
                          allowedRoles={[
                            "admin",
                            "branchadmin",
                            "teacher",
                            "superadmin",
                          ]}
                        >
                          <Courses />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="reports"
                      element={
                        <RoleGuard
                          allowedRoles={["branchadmin", "admin", "superadmin"]}
                        >
                          <BranchReports />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="branch-settings"
                      element={
                        <RoleGuard
                          allowedRoles={["branchadmin", "admin", "superadmin"]}
                        >
                          <BranchSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="admin/*"
                      element={
                        <RoleGuard
                          allowedRoles={["admin", "branchadmin", "superadmin"]}
                        >
                          <Routes>
                            <Route index element={<BranchAdminDashboard />} />
                            <Route
                              path="dashboard"
                              element={<BranchAdminDashboard />}
                            />
                            <Route
                              path="students"
                              element={<AdminStudents />}
                            />
                            <Route
                              path="teachers"
                              element={<AdminTeachers />}
                            />
                            <Route path="classes" element={<AdminClasses />} />
                            <Route path="courses" element={<AdminCourses />} />
                            <Route
                              path="departments"
                              element={<AdminDepartments />}
                            />
                            <Route
                              path="elearning"
                              element={<AdminECourseApprovals />}
                            />
                            <Route
                              path="elearning/analytics"
                              element={<AdminECourseAnalytics />}
                            />
                            <Route
                              path="elearning/students"
                              element={<AdminECourseStudents />}
                            />
                            <Route
                              path="elearning/enroll-students"
                              element={<ManualEnrollment />}
                            />
                            <Route
                              path="elearning/courses/:courseId/edit"
                              element={<TeacherCourseEdit />}
                            />
                            <Route
                              path="elearning/courses/:courseId/manage"
                              element={<CourseContentManagement />}
                            />
                            <Route path="fees" element={<AdminFees />} />
                            <Route
                              path="expenses"
                              element={<AdminExpenses />}
                            />
                            <Route path="attendance" element={<Attendance />} />
                            <Route
                              path="announcements"
                              element={<AdminAnnouncements />}
                            />
                            <Route path="reports" element={<BranchReports />} />
                          </Routes>
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="secretary/*"
                      element={
                        <RoleGuard
                          allowedRoles={["secretary", "admin", "superadmin"]}
                        >
                          <Routes>
                            <Route index element={<SecretaryDashboard />} />
                            <Route
                              path="students"
                              element={<SecretaryStudents />}
                            />
                            <Route
                              path="payments"
                              element={<SecretaryFees />}
                            />
                            <Route path="fees" element={<SecretaryFees />} />
                            <Route
                              path="schedule"
                              element={<SecretarySchedule />}
                            />
                            <Route
                              path="announcements"
                              element={<SecretaryAnnouncements />}
                            />
                            <Route
                              path="attendance"
                              element={<SecretaryAttendance />}
                            />
                            <Route
                              path="reports"
                              element={<SecretaryReports />}
                            />
                            <Route
                              path="expenses"
                              element={<SecretaryExpenses />}
                            />
                          </Routes>
                        </RoleGuard>
                      }
                    />

                    <Route
                      path="superadmin"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <SuperAdminPanel />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="terms"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <TermManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="landing-page"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <LandingPageManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="branches"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <BranchManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <UserManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="expense-management"
                      element={
                        <RoleGuard allowedRoles={["superadmin", "branchadmin"]}>
                          <ExpenseManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="financial-reports"
                      element={
                        <RoleGuard allowedRoles={["admin", "superadmin"]}>
                          <FinancialReports />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="system-analytics"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <SystemAnalytics />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <GlobalSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="security"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <SecurityRoles />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="audit-logs"
                      element={
                        <RoleGuard allowedRoles={["superadmin"]}>
                          <AuditLogs />
                        </RoleGuard>
                      }
                    />
                    <Route path="debug-auth" element={<DebugAuth />} />
                  </Route>
                </Routes>
              </div>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                }}
              />
            </BranchProvider>
          </AuthProvider>
        </OfflineProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
